import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { InvalidationKey, keyId } from '../lib/wsInvalidationMap';

/** Default trailing-edge coalescing window. */
const DEFAULT_COALESCE_MS = 400;

/**
 * THUB-182: batch query-key invalidations onto a trailing-edge timer.
 *
 * Why this is needed: `dispatchBroadcast` in WebSocketContext is a synchronous
 * forEach fired straight from `ws.onmessage`, with no debounce anywhere in the
 * path, and `file_ready` is broadcast once per file. Fifty files landing in two
 * seconds previously produced 200 immediate refetches per connected browser
 * (50 events x 4 query keys), each an authenticated API Gateway call.
 *
 * With this, keys accumulate in a Set and flush once per window: 50 `file_ready`
 * events collapse to ONE invalidation of ['tasks'].
 *
 * Hand-rolled on purpose — no debounce library is in web/package.json and the
 * slate must not add a dependency. In-repo precedent for hand-rolled timers:
 * WebSocketContext's keepalive, TaskExecutionMonitor's poll loop, Chart's
 * resize timer.
 */
export function useCoalescedInvalidate(windowMs: number = DEFAULT_COALESCE_MS) {
  const queryClient = useQueryClient();
  // Map keyId -> key, so repeats within a window collapse to one entry.
  const pendingRef = useRef<Map<string, InvalidationKey>>(new Map());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    timerRef.current = null;
    const keys = Array.from(pendingRef.current.values());
    pendingRef.current.clear();
    for (const queryKey of keys) {
      queryClient.invalidateQueries({ queryKey: queryKey as unknown[] });
    }
  }, [queryClient]);

  /** Queue keys for invalidation; flushes once, `windowMs` after the FIRST queue. */
  const schedule = useCallback((keys: InvalidationKey[]) => {
    if (keys.length === 0) return;
    for (const key of keys) {
      pendingRef.current.set(keyId(key), key);
    }
    // Trailing edge relative to the first event in the burst: a continuous
    // stream still flushes every windowMs instead of being starved forever
    // (which a reset-on-every-event debounce would do).
    if (timerRef.current === null) {
      timerRef.current = setTimeout(flush, windowMs);
    }
  }, [flush, windowMs]);

  // Drop any pending timer on unmount — never invalidate after teardown.
  useEffect(() => {
    // Capture the pending Map up front: reading `pendingRef.current` inside the cleanup
    // trips react-hooks/exhaustive-deps (the ref may point at a different value by the time
    // cleanup runs), and CI builds with CI=true treat that warning as a build error. The
    // captured reference is the same Map for this hook instance's lifetime, so clearing it
    // is equivalent.
    const pending = pendingRef.current;
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      pending.clear();
    };
  }, []);

  return schedule;
}

/**
 * THUB-182: trailing-edge coalescer for an arbitrary callback.
 *
 * The detail modals (ConnectionDetails / PartnerDetails) hold their task and
 * connection lists in component-local `useState`, entirely outside TanStack —
 * so `invalidateQueries` can never reach them, and an open modal never updated.
 * They therefore need their own WebSocket subscription that re-runs their local
 * fetch. That fetch must be coalesced for exactly the same reason as the query
 * keys above: a bulk file drop would otherwise re-fire it once per file.
 *
 * The latest arguments win; the callback runs at most once per window.
 */
export function useCoalescedCallback<A extends any[]>(
  fn: (...args: A) => void | Promise<void>,
  windowMs: number = DEFAULT_COALESCE_MS
): (...args: A) => void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const argsRef = useRef<A | null>(null);
  // Keep the newest fn without re-creating the returned callback (which would
  // otherwise churn the useEffect that registers the WS subscription).
  const fnRef = useRef(fn);
  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      argsRef.current = null;
    };
  }, []);

  return useCallback((...args: A) => {
    argsRef.current = args;
    if (timerRef.current !== null) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const pending = argsRef.current;
      argsRef.current = null;
      if (pending) {
        void fnRef.current(...pending);
      }
    }, windowMs);
  }, [windowMs]);
}
